import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SeoManager, getPageSeo, buildFAQSchema } from '../seo';
import '../styles/ClickTestsPage.css';
import ClickTestExtras from '../components/ClickTestExtras';
import TypingSpeedClassifications from '../components/TypingSpeedClassifications';
import FAQSection, { clickFaqs } from '../components/FAQSection';

const DURATION_LABELS = {
  1: '1s',
  5: '5s',
  15: '15s',
  30: '30s',
  60: '60s',
};

const DURATION_ROUTES = {
  1: '/1s-cps-click-test/',
  5: '/5s-cps-click-test/',
  15: '/15s-cps-click-test/',
  30: '/30s-cps-click-test/',
  60: '/60s-cps-click-test/',
};

const DURATION_COPY = {
  1: {
    h1: '1 Second CPS Burst Test',
    p: 'Free 1 second CPS test. Not a typing test and not the standard 5 second click test. Score peak jitter or butterfly speed in 1.00s, then use 5s for a normal clicks-per-second score.',
  },
  5: {
    h1: '5 Second Click Test',
    p: 'Free 5 second click test. The standard CPS score: how fast you can click in 5.00 seconds. Get clicks per second, total clicks, and a Cheetah-to-Snail rank. Not a typing test.',
  },
  15: {
    h1: '15 Second Click Test',
    p: 'Free 15 second click test. A CPS stamina check after the first burst — not a 1-second peak and not a typing test. See if your clicks per second holds.',
  },
  30: {
    h1: '30 Second Click Test',
    p: 'Free 30 second click test. Mouse stamina CPS rather than a 5 second click test burst. Not a typing test.',
  },
  60: {
    h1: '1 Minute Click Test — Click Marathon',
    p: 'Free click marathon: a 1 minute click test for stamina CPS. Count total clicks and average clicks per second over 60 seconds, not a 5 second click test burst.',
  },
};

const FAQ_HEADINGS = {
  1: '1 Second CPS Test FAQs',
  5: '5 Second Click Test FAQs',
  15: '15 Second Click Test FAQs',
  30: '30 Second Click Test FAQs',
  60: '1 Minute Click Test FAQs',
};

const DURATIONS = [1, 5, 15, 30, 60];

export default function ClickSpeedTestPage({ duration = 5 }) {
  const [testDuration, setTestDuration] = useState(duration);
  const [timeLeft, setTimeLeft] = useState(duration);
  const [clicks, setClicks] = useState(0);
  const clicksRef = useRef(0);
  const [testRunning, setTestRunning] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const timerRef = useRef(null);
  const clickAreaRef = useRef(null);
  const clicksDisplayRef = useRef(null);
  const hasNavigatedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const configKey = `${duration}s-cps-click-test`;
  const seo = getPageSeo(configKey);

  // State to prevent flicker before navigation
  const [isNavigating, setIsNavigating] = useState(false);

  // Detect if device is touch
  const isTouchDevice = typeof window !== 'undefined' && (
    'ontouchstart' in window || navigator.maxTouchPoints > 0
  );

  // Update duration when prop changes
  useEffect(() => {
    setTestDuration(duration);
    setTimeLeft(duration);
    setClicks(0);
    setShowResults(false);
    setTestRunning(false);
    setIsNavigating(false); // Reset navigating state
    if (timerRef.current) clearInterval(timerRef.current);
  }, [duration]);

  const handleClickArea = () => {
    if (!testRunning) return;
    clicksRef.current += 1;
    if (clicksDisplayRef.current) {
      clicksDisplayRef.current.textContent = String(clicksRef.current);
    }
  };

  // Start test with specific duration
  const startClickTest = (newDuration) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTestDuration(newDuration);
    setTimeLeft(newDuration);
    setClicks(0);
    clicksRef.current = 0;
    setShowResults(false);
    setTestRunning(false);
    setIsNavigating(false); // Reset navigating state
  };

  // Start timer on first click
  const handleStart = () => {
    if (testRunning) return;
    setTestRunning(true);
    setShowResults(false);
    setClicks(1); // First click
    clicksRef.current = 1;
    hasNavigatedRef.current = false;
    setIsNavigating(false); // Ensure navigating state is false at start
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          setTestRunning(false);
          setShowResults(true);
          if (!hasNavigatedRef.current) {
            hasNavigatedRef.current = true;
            const finalClicks = clicksRef.current;
            const finalCps = (finalClicks / testDuration).toFixed(2);
            console.log('Final clicks:', finalClicks, 'Final CPS:', finalCps);
            
            setIsNavigating(true); // Set state to true before navigating

            navigate('/click-test-results', {
              state: {
                cps: finalCps,
                clicks: finalClicks,
                duration: testDuration,
              },
            });
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  // Clean up timer
  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  return (
    <>
      <SeoManager
        title={seo?.title}
        description={seo?.description}
        keywords={seo?.keywords}
        path={seo?.path}
        structuredData={[seo?.structuredData, buildFAQSchema(clickFaqs)].filter(Boolean)}
      />
      {!isNavigating && (
        <div className="clicktest-container">
          <h1>{(DURATION_COPY[testDuration] || {}).h1 || `${DURATION_LABELS[testDuration] || `${testDuration}s`} CPS Test`}</h1>
          <p>{(DURATION_COPY[testDuration] || {}).p || 'This is a click speed test (clicks per second), not a typing test. Pick a duration and click as fast as you can.'}</p>
          <div className="controls">
            {DURATIONS.map(d => (
              <button
                key={d}
                className={testDuration === d ? 'active' : ''}
                onClick={() => {
                  if (location.pathname !== DURATION_ROUTES[d]) {
                    navigate(DURATION_ROUTES[d]);
                  }
                }}
                disabled={testRunning}
              >
                {DURATION_LABELS[d]}
              </button>
            ))}
          </div>
          <div
            className={`click-area${testRunning ? ' running' : ''}`}
            ref={clickAreaRef}
            onClick={testRunning ? handleClickArea : handleStart}
            style={{ cursor: testRunning || !showResults ? 'pointer' : 'default' }}
            tabIndex={0}
            aria-label="Click area for speed test"
          >
            <p id="clickText">{!testRunning && !showResults ? (isTouchDevice ? 'Tap to Start!' : 'Click to Start!') : testRunning ? (isTouchDevice ? 'Tap!' : 'Click!') : ''}</p>
          </div>
          <div className="metrics">
            <div>Time: <span>{timeLeft}</span>s</div>
            <div>Clicks: <span ref={clicksDisplayRef}>{clicks}</span></div>
          </div>
          {timeLeft === 0 && !isNavigating && (
            <button className="try-again-btn" onClick={() => startClickTest(testDuration)} style={{marginTop: 24}}>Try Again</button>
          )}
        </div>
      )}
      
      {!isNavigating && <ClickTestExtras duration={testDuration} />}
      {!isNavigating && <TypingSpeedClassifications mode="click" />}
      {!isNavigating && <FAQSection mode="click" heading={FAQ_HEADINGS[testDuration]} />}
    </>
  );
} 